import React from 'react';
import { connect } from 'react-redux';
import { t } from '@lingui/macro';
import * as T from '../../constants/types';
import { withDisplayedLayersCheck } from '../../HOC/withDisplayedLayersCheck';
import { getDateRangeFromTile, removeMetadataQueryLayer } from '../../utils/dateRangeFromTile';
import { getSavedDateRange, saveDateRange } from '../../utils/dateRangeStorage';
import { MapTimeline } from './MapTimeline';

/**
 * Привязка MapTimeline к слою «Старты по часам».
 *
 * Оверлей внизу карты, рулящий тем же redux-состоянием selectedHour/selectedDate,
 * что и контрол в сайдбаре (StartsByHourFilter). Вся работа по карте (setFilter +
 * анимация радиусов через feature-state) остаётся в эффектах StartsByHourFilter
 * — это и есть per-layer контроллер; таймлайн лишь двигает курсор и дату.
 *
 * Монтируется только когда слой активен (withDisplayedLayersCheck).
 */

const SOURCE_ID = 'martin.geo_starts_mvt';
// Часы 0–23 как categorical-домен
const HOURS_DOMAIN = {
    type: 'categorical',
    values: Array.from({ length: 24 }, (_, h) => h),
    format: h => `${String(h).padStart(2, '0')}:00`,
};
const StartsByHourTimelineComponent = ({ selectedHour, setSelectedHour, selectedDate, setSelectedDate }) => {
    // Дублирующий дроп selectedHour (playback шлёт каждый кадр) гасим: диспатчим
    // только при реальной смене часа, чтобы не дёргать redux/перерисовку зря.
    const lastHourRef = React.useRef(selectedHour);
    const onCursorChange = hour => {
        if (hour === lastHourRef.current) return;
        lastHourRef.current = hour;
        setSelectedHour(hour);
    };
    // Диапазон доступных дат из метаданных тайлов (как в DateSelector сайдбара)
    const [ dateRange, setDateRange ] = React.useState(() => getSavedDateRange());
    React.useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        const map = window.__map;
        if (!map) return () => {};

        const load = async () => {
            try {
                const range = await getDateRangeFromTile(map, SOURCE_ID);
                if (range && range.minDate && range.maxDate) {
                    setDateRange(range);
                    saveDateRange(range);
                }
            } catch (err) {
                // оставляем сохранённый диапазон
            }
        };
        const onSourceData = e => {
            if (e.sourceId === SOURCE_ID && e.isSourceLoaded) load();
        };
        const timeoutId = setTimeout(load, 500);
        map.on('sourcedata', onSourceData);
        return () => {
            clearTimeout(timeoutId);
            map.off('sourcedata', onSourceData);
            // Компонент размонтируется, когда слой выключают (withDisplayedLayersCheck) —
            // убираем форсер-слой метаданных, иначе источник геостартов продолжит
            // грузиться на каждый пан/зум даже после выключения слоя.
            removeMetadataQueryLayer(map, SOURCE_ID);
        };
    }, []);

    return (
        <MapTimeline
            domain={HOURS_DOMAIN}
            cursor={selectedHour}
            onCursorChange={onCursorChange}
            playback={{ enabled: true, speed: 1.5 }}
            minorTicks
            dateControl={{
                value: selectedDate,
                onChange: setSelectedDate,
                minDate: dateRange.minDate,
                maxDate: dateRange.maxDate,
            }}
        />
    );
};
const mapStateToProps = ({ map: { selectedHour, selectedDate } }) => ({ selectedHour, selectedDate });
const mapDispatchToProps = dispatch => ({
    setSelectedHour: hour => dispatch({
        type: T.SET_SELECTED_HOUR,
        payload: { selectedHour: hour },
    }),
    setSelectedDate: date => dispatch({
        type: T.SET_SELECTED_DATE,
        payload: { selectedDate: date },
    }),
});
const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(StartsByHourTimelineComponent);

export const StartsByHourTimeline = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Старты по часам`
);
